import type { tUpdateWapiTemplateEvent } from 'src/providers/SocketsProvider/Sockets.types';
import type { eTemplatesTable } from './TemplatesTable.enum';
import type {
  tTemplateRejectionInfo,
  tTemplateTable,
} from './TemplatesTable.types';

/**
 * The prefix every template generated by the onboarding AI pipeline starts with.
 *
 * The backend constant is `AITemplateNamePrefix = "ls_ai"` without the trailing
 * underscore (`TemplateSpamGuards.go`); the separator arrives from the
 * `strings.Join(parts, "_")` that assembles the rest of the name. Matching
 * `ls_ai_` here is deliberate — it is the boundary that keeps a customer
 * template legitimately called `ls_aidea` from reading as generated.
 *
 * It is the only signal the frontend has: the templates endpoint carries no
 * "generated by AI" flag, and the backend itself tells a generated batch apart
 * from anything the customer authored by this same prefix.
 */
const AI_TEMPLATE_NAME_PREFIX = 'ls_ai_';

/** Whether this template came out of the AI generation pipeline. */
export function isAiTemplateName(name: string): boolean {
  return name.startsWith(AI_TEMPLATE_NAME_PREFIX);
}

/**
 * The name without the plumbing: `ls_ai_post_servicio` reads as `post_servicio`.
 *
 * Only the prefix comes off, and the second segment is NOT a category.
 *
 * `BuildAITemplateName(category, slug, counter)` in `TemplateSpamGuards.go` does
 * describe an `ls_ai_<category>_<slug>_<n>` shape, but it has no production
 * caller — only three backend test files reference it. The name that reaches us
 * is whatever the generator produced, stored verbatim (`content.Name`, trimmed,
 * in `AITemplateDraftBuilder.go`) and submitted as-is. Real data agrees:
 * `ls_ai_post_servicio` carries neither a category segment nor a counter.
 *
 * So peeling a leading `marketing`/`utility` would delete a real word — a
 * template about digital marketing is named `ls_ai_marketing_digital` — and a
 * trailing digit is just as likely to belong to the name (`promo_2x1`).
 *
 * Even if some names did carry the category, it would be a snapshot of
 * generation time and unsafe to display: Meta recategorises templates, at
 * creation and long after (`template_category_update`), while a Meta template
 * name can never change. The live category is the table's own column.
 *
 * Underscores stay. Meta does not accept accents or ñ in a template name, so the
 * backend folds them away at generation time (`stripDiacritics`) and they cannot
 * be recovered here. Left as a slug, `recordatorio_visita_manana` reads as the
 * identifier it is; respaced into "Recordatorio visita manana" it would read as
 * misspelled Spanish. A genuinely written title has to come from the generator.
 *
 * A name without the prefix is returned untouched — templates the advisor wrote
 * keep theirs exactly as they typed it.
 */
export function stripAiTemplateNamePrefix(name: string): string {
  if (!isAiTemplateName(name)) return name;

  const stripped = name.slice(AI_TEMPLATE_NAME_PREFIX.length);
  // A name that is nothing but the prefix would otherwise render as an empty
  // cell, which is worse than showing the raw name.
  return stripped === '' ? name : stripped;
}

/**
 * INT-1555 — Meta's `rejection_info` as the table keeps it: null when it is
 * absent, null, or carries no text at all, so the detail falls back to our
 * generic copy instead of showing an empty message.
 */
export function toRejectionInfo(
  raw:
    | { reason?: string | null; recommendation?: string | null }
    | null
    | undefined,
): tTemplateRejectionInfo | null {
  const reason = (raw?.reason ?? '').trim();
  const recommendation = (raw?.recommendation ?? '').trim();
  if (!reason && !recommendation) return null;
  return { reason, recommendation };
}

/**
 * INT-1555 — one row after a `whatsapp_business.template.status_update`
 * event. Every event replaces the rejection fields with what it carries, as
 * WBS does: an approval after an edit arrives without `rejection_info` and
 * clears Meta's earlier recommendation.
 */
export function applyTemplateStatusUpdate(
  template: tTemplateTable,
  event: tUpdateWapiTemplateEvent,
): tTemplateTable {
  return {
    ...template,
    status: event.status as eTemplatesTable,
    reason: event?.reason,
    rejectionInfo: toRejectionInfo(event?.rejection_info),
  };
}
